import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Badge, Alert, Table } from 'react-bootstrap';
import PlayerPicker from './PlayerPicker';
import { isYouTubeUrl } from '../utils/youtube';
import {
    WALKOVER_NONE,
    WALKOVER_GUEST_FORFEIT,
    WALKOVER_HOME_FORFEIT,
    normalizeWalkover,
    buildWalkoverSets,
    getWalkoverLabel,
    getWalkoverScoreLabel
} from '../utils/walkover';
import './ScoreEntryModal.css';

const ScoreEntryModal = ({
    match,
    lineupItem,
    homePlayers = [],
    guestPlayers = [],
    initialData = null,
    isEditing = false,
    videoOnly = false,
    onSave,
    onCancel
}) => {
    const [formData, setFormData] = useState({
        math_index: lineupItem?.index || 1,
        math_type: lineupItem?.type || 1,
        player_1: '',
        player_2: '',
        player_3: '',
        player_4: '',
        sets: Array(12).fill(''),
        video: '',
        walkover: WALKOVER_NONE
    });
    const [errors, setErrors] = useState({});

    // Khởi tạo data
    useEffect(() => {
        if (initialData && isEditing) {
            setFormData({
                ...initialData,
                walkover: normalizeWalkover(initialData.walkover),
                sets: [
                    initialData.home_sec_1 || '', initialData.guest_sec_1 || '',
                    initialData.home_sec_2 || '', initialData.guest_sec_2 || '',
                    initialData.home_sec_3 || '', initialData.guest_sec_3 || '',
                    initialData.home_sec_4 || '', initialData.guest_sec_4 || '',
                    initialData.home_sec_5 || '', initialData.guest_sec_5 || '',
                    initialData.home_result || '', initialData.guest_result || ''
                ]
            });
        } else if (lineupItem) {
            setFormData(prev => ({
                ...prev,
                math_index: lineupItem.index,
                math_type: lineupItem.type
            }));
        }
    }, [initialData, isEditing, lineupItem]);

    const walkover = normalizeWalkover(formData.walkover);
    const isWalkoverMode = walkover !== WALKOVER_NONE;

    const handleWalkoverChange = (nextWalkover) => {
        const target = walkover === nextWalkover ? WALKOVER_NONE : nextWalkover;
        setErrors({});

        if (target === WALKOVER_NONE) {
            setFormData(prev => ({
                ...prev,
                walkover: WALKOVER_NONE,
                player_1: '',
                player_2: '',
                player_3: '',
                player_4: '',
                sets: Array(12).fill('')
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            walkover: target,
            player_1: '',
            player_2: '',
            player_3: '',
            player_4: '',
            sets: buildWalkoverSets(target)
        }));
    };

    const handleSetsChange = (index, value) => {
        const newSets = [...formData.sets];
        newSets[index] = value;

        // Auto-calculate final result (sets[10] và sets[11])
        let homeWins = 0, guestWins = 0;
        for (let i = 0; i < 10; i += 2) {
            const homeScore = parseInt(newSets[i] || 0);
            const guestScore = parseInt(newSets[i + 1] || 0);
            if (homeScore > guestScore) {
                homeWins++;
            } else if (guestScore > homeScore) {
                guestWins++;
            }
        }
        newSets[10] = homeWins;
        newSets[11] = guestWins;

        setFormData(prev => ({ ...prev, sets: newSets }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (videoOnly) {
            if (!formData.video || !formData.video.trim()) {
                newErrors.video = 'Vui lòng nhập link YouTube';
            }
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        }
        if (isWalkoverMode) {
            // Bỏ cuộc không cần chọn VĐV, tỉ số do hệ thống đặt sẵn.
            setErrors({});
            return true;
        }
        if (!formData.player_1) newErrors.player_1 = 'Thiếu VĐV đội nhà';
        if (!formData.player_3) newErrors.player_3 = 'Thiếu VĐV đội khách';
        if (formData.math_type === 2) {
            if (!formData.player_2) newErrors.player_2 = 'Thiếu VĐV 2 đội nhà';
            if (!formData.player_4) newErrors.player_4 = 'Thiếu VĐV 2 đội khách';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onSave({ ...formData, walkover });
        }
    };

    return (
        <Modal show={true} onHide={onCancel} size="lg" className="score-entry-modal" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {videoOnly
                        ? `${initialData?.video ? 'Cập nhật' : 'Bổ sung'} video - Trận ${lineupItem?.index}`
                        : `${isEditing ? 'Sửa' : 'Nhập'} Điểm - Trận ${lineupItem?.index} (${formData.math_type === 1 ? 'Đơn' : 'Đôi'})`}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {Object.keys(errors).length > 0 && (
                    <Alert variant="danger">
                        {videoOnly ? errors.video : 'Vui lòng chọn đầy đủ VĐV trước khi lưu!'}
                    </Alert>
                )}

                {videoOnly && (
                    <Alert variant="info" className="py-2">
                        Chỉ link video được cập nhật. Tỉ số, VĐV và điểm của trận không thay đổi.
                    </Alert>
                )}

                {!videoOnly && (
                    <>
                        {/* Section 0: Bỏ cuộc */}
                        <div className="walkover-section mb-3">
                            <div className="walkover-bar">
                                <span className="walkover-label">Bỏ cuộc:</span>
                                <Button
                                    variant={walkover === WALKOVER_HOME_FORFEIT ? 'danger' : 'outline-danger'}
                                    size="sm"
                                    className="walkover-btn"
                                    onClick={() => handleWalkoverChange(WALKOVER_HOME_FORFEIT)}
                                >
                                    Đội nhà bỏ cuộc (0-3)
                                </Button>
                                <Button
                                    variant={walkover === WALKOVER_GUEST_FORFEIT ? 'danger' : 'outline-danger'}
                                    size="sm"
                                    className="walkover-btn"
                                    onClick={() => handleWalkoverChange(WALKOVER_GUEST_FORFEIT)}
                                >
                                    Đội khách bỏ cuộc (3-0)
                                </Button>
                                {isWalkoverMode && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="walkover-btn"
                                        onClick={() => handleWalkoverChange(WALKOVER_NONE)}
                                    >
                                        Huỷ bỏ cuộc
                                    </Button>
                                )}
                            </div>
                            {isWalkoverMode && (
                                <Alert variant="warning" className="walkover-alert mt-2 mb-0">
                                    <strong>{getWalkoverLabel(walkover)}</strong> — {walkover === WALKOVER_GUEST_FORFEIT ? match.home_team_name : match.guest_team_name} thắng {getWalkoverScoreLabel(walkover)}.
                                    <div className="small mt-1">
                                        Không ghi nhận VĐV nào nên điểm tích luỹ cá nhân không thay đổi. Điểm đồng đội vẫn được tính bình thường.
                                    </div>
                                </Alert>
                            )}
                        </div>

                        {/* Section 1: Chọn Cầu Thủ */}
                        <div className={`lineup-section mb-4${isWalkoverMode ? ' section-disabled' : ''}`}>
                            <Row>
                                <Col xs={12} md={6} className="mb-3 mb-md-0">
                                    <div className="team-box home-team">
                                        <h5><Badge bg="primary">Nhà: {match.home_team_name}</Badge></h5>
                                        <PlayerPicker
                                            players={homePlayers}
                                            selectedPlayers={formData.math_type === 1
                                                ? (formData.player_1 ? [formData.player_1] : [])
                                                : [formData.player_1, formData.player_2].filter(Boolean)
                                            }
                                            onSelect={(selected) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    player_1: selected[0] || '',
                                                    player_2: formData.math_type === 2 ? (selected[1] || '') : ''
                                                }));
                                            }}
                                            maxSelection={formData.math_type === 1 ? 1 : 2}
                                            disabled={isWalkoverMode}
                                        />
                                    </div>
                                </Col>
                                <Col xs={12} md={6}>
                                    <div className="team-box guest-team">
                                        <h5><Badge bg="info">Khách: {match.guest_team_name}</Badge></h5>
                                        <PlayerPicker
                                            players={guestPlayers}
                                            selectedPlayers={formData.math_type === 1
                                                ? (formData.player_3 ? [formData.player_3] : [])
                                                : [formData.player_3, formData.player_4].filter(Boolean)
                                            }
                                            onSelect={(selected) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    player_3: selected[0] || '',
                                                    player_4: formData.math_type === 2 ? (selected[1] || '') : ''
                                                }));
                                            }}
                                            maxSelection={formData.math_type === 1 ? 1 : 2}
                                            disabled={isWalkoverMode}
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Section 2: Bảng Nhập Điểm */}
                        <div className={`score-table-section${isWalkoverMode ? ' section-disabled' : ''}`}>
                            <div className="table-responsive">
                                <Table bordered className="score-table">
                                    <thead>
                                        <tr>
                                            <th>Đội</th>
                                            <th>S1</th><th>S2</th><th>S3</th><th>S4</th><th>S5</th>
                                            <th className="result-col">KQ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Row: Đội Nhà */}
                                        <tr className="home-row">
                                            <td className="team-name">Nhà</td>
                                            {[1, 2, 3, 4, 5].map((setNum) => (
                                                <td key={`home-${setNum}`}>
                                                    <Form.Control
                                                        type="number" min="0" max="99"
                                                        value={formData.sets[(setNum - 1) * 2] || ''}
                                                        onChange={(e) => handleSetsChange((setNum - 1) * 2, e.target.value)}
                                                        className="score-input"
                                                        disabled={isWalkoverMode}
                                                    />
                                                </td>
                                            ))}
                                            <td className="result-val"><strong>{formData.sets[10] || 0}</strong></td>
                                        </tr>
                                        {/* Row: Đội Khách */}
                                        <tr className="guest-row">
                                            <td className="team-name">Khách</td>
                                            {[1, 2, 3, 4, 5].map((setNum) => (
                                                <td key={`guest-${setNum}`}>
                                                    <Form.Control
                                                        type="number" min="0" max="99"
                                                        value={formData.sets[(setNum - 1) * 2 + 1] || ''}
                                                        onChange={(e) => handleSetsChange((setNum - 1) * 2 + 1, e.target.value)}
                                                        className="score-input"
                                                        disabled={isWalkoverMode}
                                                    />
                                                </td>
                                            ))}
                                            <td className="result-val"><strong>{formData.sets[11] || 0}</strong></td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </>
                )}

                <div className="mt-3">
                    <Form.Group>
                        <Form.Label>Link Video</Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="Dán link YouTube" 
                            value={formData.video || ''}
                            onChange={(e) => setFormData(prev => ({...prev, video: e.target.value}))}
                            className={formData.video && !isYouTubeUrl(formData.video) ? 'border-warning' : formData.video ? 'border-success' : ''}
                        />
                        {formData.video && !isYouTubeUrl(formData.video) ? (
                            <Form.Text className="text-warning">Link không được nhận diện là YouTube, video có thể không xem được trực tiếp.</Form.Text>
                        ) : formData.video && isYouTubeUrl(formData.video) ? (
                            <Form.Text className="text-success">Link YouTube hợp lệ ✓</Form.Text>
                        ) : null}
                    </Form.Group>
                </div>
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button variant="outline-secondary" onClick={onCancel}>Hủy / Đóng</Button>
                <Button variant="success" size="lg" onClick={handleSubmit} className="px-5">
                    {videoOnly ? "Lưu video" : "Lưu biên bản"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ScoreEntryModal;
